import {Tooltip} from "antd";
import { isMobileOnly } from 'react-device-detect';

export interface ToothProps {
    position: number,
    statusLabel?: string,
    statusColor?: string,
    headerColor?: string,
    milk: boolean,
    selected: boolean,
    multiSelectionMode: boolean,
    tooltipContent?: React.ReactNode,
    /** Suppress ONLY the hover tooltip (the black, portaled AntD tooltip) — e.g.
     *  while this tooth is the active quick-status tooth. The clinical status
     *  badge below the tooth is NEVER suppressed. */
    disableTooltip?: boolean,
}

const Tooth: React.FC<ToothProps> = (props) => {
    const isMaxillary = (!props.milk && props.position < 30) || (props.milk && props.position < 66);

    const positionLabelStyle = !props.selected && props.headerColor ? {background: props.headerColor} : {};
    const positionLabelClass = (orientation: string) =>
        'position-label ' + orientation + (!props.selected && props.statusLabel ? ' dark-header' : '');

    // The whole tooth, INCLUDING the clinical status badge. The badge lives here
    // (not in any tooltip-conditional branch) so it always renders regardless of
    // disableTooltip / hover / palette state.
    const toothContent = (
        <div className={'dentobook-tooth' + (props.selected ? ' dentobook-selected-tooth' : '') + (props.multiSelectionMode ? ' dentobook-multiselection-mode' : '')}>
            <div className={'tooth-visual'}>
                {isMaxillary &&
                    <div className={positionLabelClass('top-orientation')} style={positionLabelStyle}>
                        {props.position}
                    </div>
                }

                <div className={'tooth-image'}>
                    <img src={require(`../../assets/teeth/${props.position}.svg`)} alt={`Tooth ${props.position}`}/>
                </div>

                {!isMaxillary &&
                    <div className={positionLabelClass('bottom-orientation')} style={positionLabelStyle}>
                        {props.position}
                    </div>
                }
            </div>

            {/* Clinical status badge — ALWAYS rendered in a dedicated row directly
                below the tooth drawing whenever a status exists. Not a tooltip;
                never suppressed by hover/palette state. */}
            <div className={'tooth-status-badge-row'}>
                {props.statusLabel &&
                    <span className={'tooth-status-badge'}
                          style={!props.selected ? {border: '1px solid ' + props.statusColor} : {}}>
                        {props.statusLabel}
                    </span>
                }
            </div>
        </div>
    );

    // When the tooltip is disabled (quick-status grid, or touch) the AntD Tooltip
    // is NOT rendered at all — so its portaled overlay can never mount, not even
    // for a single frame at 0,0. (Controlling `open={false}` was not enough: AntD
    // still briefly mounts the portal.) The status badge lives in toothContent, so
    // it always renders regardless.
    if (isMobileOnly || props.disableTooltip) {
        return toothContent;
    }

    return (
        <Tooltip
            title={props.tooltipContent}
            placement="bottom"
            mouseEnterDelay={0.4}
            destroyTooltipOnHide
        >
            {toothContent}
        </Tooltip>
    );
}

export default Tooth;
